import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore'
import Database from '../types/Database'
import { validateBankDetails } from '../utils/bankDetailsValidation'

/**
 * Saves bank details for a user after validating all fields.
 * Throws an error with field-level validation messages if any field is invalid.
 *
 * @param userId - The Firebase Auth UID of the user
 * @param details - The BankDetails object to save
 */
export async function saveBankDetails(
  userId: string,
  details: Database.BankDetails
): Promise<void> {
  const errors = validateBankDetails(details)
  if (errors) {
    const message = Object.entries(errors)
      .map(([field, error]) => `${field}: ${error}`)
      .join(', ')
    throw new Error(`Validation failed: ${message}`)
  }

  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, { bankDetails: details })
}

/**
 * Removes bank details from a user's document by setting the field to null.
 *
 * @param userId - The Firebase Auth UID of the user
 */
export async function removeBankDetails(userId: string): Promise<void> {
  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, { bankDetails: null })
}

/**
 * Retrieves bank details for a user from their document.
 * Returns null if the user has no bank details set.
 *
 * @param userId - The Firebase Auth UID of the user
 * @returns The BankDetails object or null
 */
export async function getBankDetails(
  userId: string
): Promise<Database.BankDetails | null> {
  const db = getFirestore()
  const userRef = doc(db, 'users', userId)
  const snap = await getDoc(userRef)

  if (!snap.exists()) {
    return null
  }

  const data = snap.data()
  return data?.bankDetails ?? null
}
